import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  IDefaultRulePropagationApplyRequest,
  IDefaultRulePropagationPreview,
  IDefaultRulePropagationRow,
} from "types/DefaultRules/DefaultRules";

const Section = styled.div`
  margin-bottom: 1.25rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const Row = styled.label<{ $skipped?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 0.25rem;
  cursor: ${(p) => (p.$skipped ? "not-allowed" : "pointer")};
  background: ${(p) => (p.$skipped ? "#f9fafb" : "#fff")};
  opacity: ${(p) => (p.$skipped ? 0.75 : 1)};

  .meta {
    flex: 1;
    min-width: 0;
    font-size: 13px;
  }

  .label {
    font-weight: 600;
  }

  .reason {
    color: #6b7280;
    font-size: 12px;
    margin-top: 2px;
  }
`;

const Empty = styled.div`
  color: #6b7280;
  font-size: 13px;
  padding: 0.5rem 0;
`;

type PropagateDefaultModalProps = {
  visible: boolean;
  onHide: () => void;
  title?: string;
  preview?: IDefaultRulePropagationPreview;
  isLoading?: boolean;
  onApply: (
    requests: IDefaultRulePropagationApplyRequest[]
  ) => Promise<void> | void;
  isApplying?: boolean;
};

function PropagateDefaultModal({
  visible,
  onHide,
  title = "Propagate default changes",
  preview,
  isLoading,
  onApply,
  isApplying,
}: PropagateDefaultModalProps) {
  const [selected, setSelected] = useState<Record<string, boolean>>({});

  /**
   * Map from child id to its bucket in the preview. This is what we submit to apply
   * so the backend can detect drift between preview and apply.
   */
  const bucketById = useMemo(() => {
    const map = new Map<string, IDefaultRulePropagationRow["bucket"]>();
    if (preview) {
      preview.buckets.full.forEach((r) => map.set(r.id, "full"));
      preview.buckets.codeListOnly.forEach((r) =>
        map.set(r.id, "codeListOnly")
      );
      preview.buckets.skipped.forEach((r) => map.set(r.id, "skipped"));
    }
    return map;
  }, [preview]);

  const eligibleIds = useMemo(() => {
    if (!preview) return [] as string[];
    return [
      ...preview.buckets.full.map((r) => r.id),
      ...preview.buckets.codeListOnly.map((r) => r.id),
    ];
  }, [preview]);

  useEffect(() => {
    if (!preview) return;
    const next: Record<string, boolean> = {};
    eligibleIds.forEach((id) => {
      next[id] = true;
    });
    setSelected(next);
  }, [preview, eligibleIds]);

  const toggle = (id: string) =>
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const selectedRequests: IDefaultRulePropagationApplyRequest[] =
    useMemo(() => {
      return Object.keys(selected)
        .filter((id) => selected[id])
        .map((id) => ({
          id,
          expectedBucket: bucketById.get(id) ?? "full",
        }));
    }, [selected, bucketById]);

  const describe = (r: IDefaultRulePropagationRow) => {
    const label = r.name || r.groupName || r.id.slice(0, 8);
    const meta: string[] = [];
    if (r.providerName) meta.push(r.providerName);
    else if (r.providerId) meta.push(`provider ${r.providerId.slice(0, 8)}`);
    if (r.payerName) meta.push(r.payerName);
    else if (r.payerId !== null && r.payerId !== undefined)
      meta.push(`payer ${r.payerId}`);
    return { label, meta: meta.join(" · ") };
  };

  const renderBucket = (
    bucketName: "full" | "codeListOnly" | "skipped",
    rows: IDefaultRulePropagationRow[]
  ) => {
    const bucketLabels: Record<typeof bucketName, string> = {
      full: "Full update",
      codeListOnly: "Code list only (terms preserved)",
      skipped: "Skipped (diverged code list)",
    };
    const bucketSeverities: Record<
      typeof bucketName,
      "success" | "warning" | "danger"
    > = {
      full: "success",
      codeListOnly: "warning",
      skipped: "danger",
    };
    const headerText = bucketLabels[bucketName];
    const severity = bucketSeverities[bucketName];
    return (
      <Section>
        <SectionHeader>
          <Tag value={headerText} severity={severity} />
          <span>{rows.length}</span>
        </SectionHeader>
        {rows.length === 0 ? (
          <Empty>No copies in this bucket.</Empty>
        ) : (
          rows.map((r) => {
            const { label, meta } = describe(r);
            const disabled = bucketName === "skipped";
            return (
              <Row key={r.id} $skipped={disabled} aria-disabled={disabled}>
                <Checkbox
                  checked={disabled ? false : !!selected[r.id]}
                  disabled={disabled}
                  onChange={() => !disabled && toggle(r.id)}
                />
                <div className="meta">
                  <div className="label">{label}</div>
                  {meta && <div className="reason">{meta}</div>}
                  <div className="reason">{r.reason}</div>
                </div>
              </Row>
            );
          })
        )}
      </Section>
    );
  };

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isApplying}
      />
      <Button
        label={`Apply to ${selectedRequests.length} selected`}
        icon="pi pi-check"
        onClick={() => onApply(selectedRequests)}
        disabled={isApplying || selectedRequests.length === 0}
        loading={isApplying}
      />
    </div>
  );

  return (
    <Dialog
      header={title}
      visible={visible}
      onHide={onHide}
      style={{ width: "640px", maxWidth: "95vw" }}
      footer={footer}
    >
      {isLoading || !preview ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "2rem" }}
        >
          <ProgressSpinner style={{ width: 40, height: 40 }} />
        </div>
      ) : (
        <>
          <p style={{ fontSize: 13, color: "#4b5563", marginTop: 0 }}>
            {preview.total} copies linked to this default. <strong>Full</strong>{" "}
            copies will have both code list and terms overwritten.
            <br />
            <strong>Code-list-only</strong> copies have local term edits — only
            the code list will be overwritten. <strong>Skipped</strong> copies
            have diverged code lists and are not eligible.
          </p>
          {renderBucket("full", preview.buckets.full)}
          {renderBucket("codeListOnly", preview.buckets.codeListOnly)}
          {renderBucket("skipped", preview.buckets.skipped)}
        </>
      )}
    </Dialog>
  );
}

export default PropagateDefaultModal;
